import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/contexts/auth-context';
import { createQueryClient } from '@/lib/query-client';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { AppLayout } from '@/components/layout/app-layout';
import { DocsLayout } from '@/components/layout/docs-layout';

import LoginPage from '@/pages/login';
import RegisterPage from '@/pages/register';
import DocsIndexPage from '@/pages/docs-index';
import DocsPage from '@/pages/docs-page';
import DashboardPage from '@/pages/dashboard';
import StudentsPage from '@/pages/students';
import StudentDetailPage from '@/pages/student-detail';
import AssignmentsPage from '@/pages/assignments';
import InvitationsPage from '@/pages/invitations';
import TeachersPage from '@/pages/teachers';
import TeacherDetailPage from '@/pages/teacher-detail';
import SeedsPage from '@/pages/seeds';
import SeedDetailPage from '@/pages/seed-detail';
import PracticeSessionsPage from '@/pages/practice-sessions';
import SeedsAnalyticsPage from '@/pages/seeds-analytics';
import AIInteractionsPage from '@/pages/ai-interactions';
import SettingsPage from '@/pages/settings';
import ReportsPage from '@/pages/reports';
import ReportDetailPage from '@/pages/report-detail';
import TagsPage from '@/pages/tags';
import FunnelPage from '@/pages/funnel';
import ContentListPage from '@/pages/content';
import ContentEditorPage from '@/pages/content-editor';
import ContentTrashPage from '@/pages/content-trash';
import ContentMetricsPage from '@/pages/content-metrics';
import NotFoundPage from '@/pages/not-found';

import './index.css';

const queryClient = createQueryClient();

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  {
    element: <DocsLayout />,
    children: [
      { path: '/docs', element: <DocsIndexPage /> },
      { path: '/docs/:role', element: <DocsPage /> },
      { path: '/docs/:role/:page', element: <DocsPage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'students', element: <StudentsPage /> },
          { path: 'students/:id', element: <StudentDetailPage /> },
          { path: 'assignments', element: <AssignmentsPage /> },
          { path: 'reports', element: <ReportsPage /> },
          { path: 'reports/:id', element: <ReportDetailPage /> },
          { path: 'seeds', element: <SeedsPage /> },
          { path: 'seeds/analytics', element: <SeedsAnalyticsPage /> },
          { path: 'seeds/:id', element: <SeedDetailPage /> },
          { path: 'practice-sessions', element: <PracticeSessionsPage /> },
          { path: 'ai-interactions', element: <AIInteractionsPage /> },
          { path: 'invitations', element: <InvitationsPage /> },
          { path: 'teachers', element: <TeachersPage /> },
          { path: 'teachers/:id', element: <TeacherDetailPage /> },
          { path: 'tags', element: <TagsPage /> },
          { path: 'funnel', element: <FunnelPage /> },
          { path: 'content', element: <ContentListPage /> },
          { path: 'content/trash', element: <ContentTrashPage /> },
          { path: 'content/new', element: <ContentEditorPage /> },
          { path: 'content/:id', element: <ContentEditorPage /> },
          { path: 'content/:id/metrics', element: <ContentMetricsPage /> },
          { path: 'settings', element: <SettingsPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster richColors />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
